import clsx from 'clsx';
import { MapProvider } from 'components/Map/MapContext';
import { Map, View } from 'ol';
import { FullScreen, defaults as controlDefaults } from 'ol/control';
import Point from 'ol/geom/Point';
import { defaults as interactionDefaults } from 'ol/interaction';
import { Tile } from 'ol/layer';
import { get, transformExtent } from 'ol/proj';
import WMTS from 'ol/source/WMTS';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import React, { useEffect, useRef, useState } from 'react';
import { ControlMetaData } from 'types/mapTypes/MapModel';
import { fetchWMTSCapabilities } from 'api/olApi';

import { mOverExpand, mOutCompress } from './fullScreenEvents';
import { pushMatomoEvent } from 'utils/matomoUtils';

interface Props {
	centerCoordinates: number[];
	zoomLevel: number;
	maxZoomLevel: number;
	minZoomLevel: number;
	tileUrl: string;
	fullscreen?: ControlMetaData;
	defaultFullscreen?: boolean;
	currentPage?: string;
	heightDevice?: number;
}

let fslabel: HTMLImageElement;
let fsLabelActive: HTMLImageElement;

const MapContainer: React.FC<Props> = ({
	centerCoordinates,
	zoomLevel,
	maxZoomLevel,
	minZoomLevel,
	children,
	tileUrl,
	fullscreen,
	defaultFullscreen = false,
	currentPage = 'startsida',
	heightDevice,
}) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const hasInitializedRef = useRef(false);
	const [map, setMap] = useState<any>(null);

	const fullScreenCss =
		fullscreen?.title === 'Fullskärm'
			? 'full-screen-btn'
			: 'full-screen-btn-en';

	useEffect(() => {
		fslabel = document.createElement('img');
		fslabel.id = 'full-screen-inactive';
		fslabel.alt = 'full-screen-inactive';
		fslabel.src = '/assets/images/icon-expand.png';

		fsLabelActive = document.createElement('img');
		fsLabelActive.id = 'full-screen-active';
		fslabel.alt = 'full-screen-active';
		fsLabelActive.src = '/assets/images/icon-compress.png';

		fslabel.addEventListener(
			'mouseover',
			() => mOverExpand(fslabel, '/assets/images/icon-expand-hover.png'),
			false
		);
		fsLabelActive.addEventListener(
			'mouseover',
			() =>
				mOverExpand(fsLabelActive, '/assets/images/icon-compress-hover.png'),
			false
		);
		fslabel.addEventListener(
			'mouseout',
			() => mOutCompress(fslabel, '/assets/images/icon-expand.png'),
			false
		);
		fsLabelActive.addEventListener(
			'mouseout',
			() => mOutCompress(fsLabelActive, '/assets/images/icon-compress.png'),
			false
		);

		const handleFullScreenToggle = () => {
			const isFullScreen = document.fullscreenElement !== null;
			if (isFullScreen) {
				pushMatomoEvent({
					event: 'klick_karta',
					event_category: 'klick_karta',
					event_action: currentPage,
					event_name: 'Fullskärm',
				});
			}
		};

		document.addEventListener('fullscreenchange', handleFullScreenToggle);

		return () => {
			fslabel.removeEventListener('mouseover', () => mOverExpand, false);
			fsLabelActive.removeEventListener('mouseover', () => mOverExpand, false);
			fslabel.removeEventListener('mouseout', () => mOutCompress, false);
			fsLabelActive.removeEventListener('mouseout', () => mOutCompress, false);
			document.removeEventListener('fullscreenchange', handleFullScreenToggle);
		};
	}, []);

	useEffect(() => {
		if (!mapRef.current || hasInitializedRef.current) return;

		hasInitializedRef.current = true;

		requestAnimationFrame(() => {
			const controls = controlDefaults({
				attribution: false,
				zoom: false,
				rotate: false,
			});
			if (!defaultFullscreen) {
				controls.extend([
					new FullScreen({
						label: fslabel,
						labelActive: fsLabelActive,
						tipLabel: fullscreen?.title,
						className: fullScreenCss,
					}),
				]);
			}
			// Create map
			const mapObj = new Map({
				target: mapRef.current ?? undefined,
				view: new View({
					projection: get('EPSG:3857'),
					center: new Point(centerCoordinates).getCoordinates(),
					zoom: zoomLevel,
					minZoom: minZoomLevel,
					maxZoom: maxZoomLevel,
					extent: transformExtent([-15, 54, 51, 70], 'EPSG:4326', 'EPSG:3857'),
				}),
				layers: [],
				controls: controls,
				interactions: interactionDefaults({
					mouseWheelZoom: true,
					dragPan: true,
					doubleClickZoom: true,
					keyboard: true,
				}),
				maxTilesLoading: 4,
			});
			fetchWMTSCapabilities(tileUrl)
				.then((wmtsCaps) => {
					// Extract options from capabilities
					const opts = optionsFromCapabilities(wmtsCaps, {
						layer: 'lantmateriet:lantmateriet_base',
						matrixSet: 'EPSG:3857',
					});
					// Create WMTS source
					const source = new WMTS(opts);

					const layer = new Tile({
						source: source,
					});

					mapObj.addLayer(layer);				
					setMap(mapObj);
					// Se till att kartan ritas ordentligt
					setTimeout(() => {
						mapObj.updateSize();
					}, 100);
					})
				.catch((error) => {
					console.error('Error fetching WMTS capabilities:', error);
				});
			return () => mapObj.setTarget(undefined);			
		});
	}, [centerCoordinates, zoomLevel, minZoomLevel, maxZoomLevel]);

	useEffect(() => {
		if (!map) return;

		(map as Map).setView(
			new View({
				projection: get('EPSG:3857'),
				center: new Point(centerCoordinates).getCoordinates(),
				zoom: zoomLevel,
				minZoom: minZoomLevel,
				maxZoom: maxZoomLevel,
				extent: transformExtent([-15, 54, 51, 70], 'EPSG:4326', 'EPSG:3857'),
			})
		);
	}, [centerCoordinates, zoomLevel, minZoomLevel, maxZoomLevel, map]);

	const handleFullscreenChange = () => {
		if (!map) return;

		if (!document.fullscreenElement) {
			map.setView(
				new View({
					projection: get('EPSG:3857'),
					center: new Point(centerCoordinates).getCoordinates(),
					zoom: zoomLevel,
					minZoom: minZoomLevel,
					maxZoom: maxZoomLevel,
					extent: transformExtent([-15, 54, 51, 70], 'EPSG:4326', 'EPSG:3857'),
				})
			);
		}
	};
	/**atttaching full screen event */
	useEffect(() => {
		document.addEventListener('fullscreenchange', handleFullscreenChange);
		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, [map]);

	return (
		<MapProvider map={map}>
			<div
				id="map"
				ref={mapRef}
				className={clsx(
					'w-full',
					'transition-all ease-in-out duration-100',
					defaultFullscreen && 'full-screen-map'
				)}
			>
				{children}
			</div>
		</MapProvider>
	);
};

export default MapContainer;
